import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BedDouble, CalendarCheck2, CalendarDays, CalendarPlus, CalendarX2,
  Clock, Euro, LogIn, LogOut, Plus, TrendingUp, TrendingDown, AlertCircle, CheckCircle2,
  Gauge, BarChart3, Percent,
} from "lucide-react";
import { getDashboard } from "@/lib/dashboard.functions";

type DashboardData = Awaited<ReturnType<typeof getDashboard>>;

export const Route = createFileRoute("/_authenticated/gestion/")({
  component: Overview,
});

const fmtDate = (iso: string) =>
  new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short" }).format(new Date(iso));
const fmtDateLong = (d: Date) =>
  new Intl.DateTimeFormat("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(d);
const fmtEur = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const relTime = (iso: string) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  const rtf = new Intl.RelativeTimeFormat("es-ES", { numeric: "auto" });
  if (diff < 60) return rtf.format(-Math.round(diff), "second");
  if (diff < 3600) return rtf.format(-Math.round(diff / 60), "minute");
  if (diff < 86400) return rtf.format(-Math.round(diff / 3600), "hour");
  return rtf.format(-Math.round(diff / 86400), "day");
};

function EstadoBadge({ estado }: { estado: string }) {
  const map: Record<string, string> = {
    confirmada: "bg-emerald-100 text-emerald-800",
    pendiente: "bg-amber-100 text-amber-800",
    cancelada: "bg-muted text-muted-foreground",
    completada: "bg-blue-100 text-blue-800",
  };
  return <span className={`rounded-full px-2 py-0.5 text-xs ${map[estado] ?? "bg-secondary"}`}>{estado}</span>;
}

function StatCard({ icon: Icon, label, value, sub, to, trend }: { icon: any; label: string; value: string | number; sub?: string; to?: string; trend?: number | null }) {
  const inner = (
    <Card className="h-full transition-shadow hover:shadow-md">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</CardTitle>
        <Icon className="h-4 w-4 text-primary" />
      </CardHeader>
      <CardContent>
        <div className="font-serif text-3xl">{value}</div>
        <div className="mt-1 flex items-center gap-2 text-xs">
          {typeof trend === "number" && (
            <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-medium ${trend >= 0 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
              {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {trend >= 0 ? "+" : ""}{trend}%
            </span>
          )}
          {sub && <span className="text-muted-foreground">{sub}</span>}
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
}

function IngresosChart({ data }: { data: { mes: string; ingresos: number; noches: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.ingresos));
  return (
    <div className="flex h-40 items-end gap-2 pt-2">
      {data.map((d, i) => {
        const h = Math.round((d.ingresos / max) * 100);
        const isLast = i === data.length - 1;
        return (
          <div key={i} className="flex flex-1 flex-col items-center gap-1">
            <div className="w-full text-center text-[10px] font-medium tabular-nums text-muted-foreground">
              {d.ingresos > 0 ? fmtEur(d.ingresos) : "—"}
            </div>
            <div className="flex w-full flex-1 items-end">
              <div
                className={`w-full rounded-t transition-all ${isLast ? "bg-primary" : "bg-primary/40"}`}
                style={{ height: `${Math.max(2, h)}%` }}
                title={`${d.mes}: ${fmtEur(d.ingresos)} · ${d.noches} noches`}
              />
            </div>
            <div className="text-[11px] capitalize text-muted-foreground">{d.mes.replace(".", "")}</div>
          </div>
        );
      })}
    </div>
  );
}

function Overview() {
  const fn = useServerFn(getDashboard);
  const [data, setData] = useState<DashboardData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setEmail(data.user?.email ?? ""));
    let cancelled = false;
    const load = () => fn()
      .then((d) => { if (!cancelled) { setData(d); setErr(null); } })
      .catch((e) => { if (!cancelled) setErr(e?.message ?? "error"); });
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [fn]);

  if (err) return <p className="text-sm text-destructive">No se pudo cargar el panel: {err}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Cargando panel…</p>;

  const { kpis, serieMeses, llegadas14, salidas7, ocupPorHab, recientes, acciones, habitacionesActivas } = data;
  const todoAlDia = !acciones.pendientes && !acciones.habSinFotos && !acciones.habSinPrecio && !acciones.sinTemporadas;

  return (
    <div className="space-y-8">
      {/* Cabecera */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-serif text-2xl">Hola{email ? `, ${email}` : ""}</h1>
          <p className="text-sm capitalize text-muted-foreground">{fmtDateLong(new Date())}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm"><Link to="/gestion/reservas"><Plus className="mr-2 h-4 w-4" />Nueva reserva</Link></Button>
          <Button asChild size="sm" variant="outline"><Link to="/gestion/calendario" search={(prev: Record<string, unknown>) => ({ ...prev, tab: "bloqueos" }) as never}><CalendarX2 className="mr-2 h-4 w-4" />Bloquear fechas</Link></Button>
          <Button asChild size="sm" variant="outline"><Link to="/gestion/calendario" search={(prev: Record<string, unknown>) => (prev ?? {}) as never}><CalendarDays className="mr-2 h-4 w-4" />Ver calendario</Link></Button>
        </div>
      </div>

      {/* KPIs operativos */}
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Hoy</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={LogIn} label="Llegadas hoy" value={kpis.llegadasHoy} to="/gestion/operaciones" />
          <StatCard icon={LogOut} label="Salidas hoy" value={kpis.salidasHoy} to="/gestion/operaciones" />
          <StatCard icon={BedDouble} label="Ocupación hoy" value={`${kpis.ocupacionHoyPct}%`} sub={`${habitacionesActivas} habitaciones activas`} to="/gestion/calendario" />
          <StatCard icon={Clock} label="Pendientes" value={kpis.pendientes} sub="Por confirmar" to="/gestion/reservas" />
        </div>
      </div>

      {/* KPIs económicos */}
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Este mes</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={Euro} label="Ingresos del mes" value={fmtEur(kpis.ingresosMes)}
            sub={kpis.ingresosMesAnterior > 0 ? `vs ${fmtEur(kpis.ingresosMesAnterior)} mes ant.` : "sin datos mes anterior"}
            trend={kpis.variacionIngresosPct}
            to="/gestion/informes" />
          <StatCard icon={Percent} label="Ocupación mensual" value={`${kpis.ocupacionMesPct}%`}
            sub={`${kpis.nochesVendidasMes}/${kpis.nochesDisponiblesMes} noches`}
            to="/gestion/calendario" />
          <StatCard icon={Gauge} label="ADR" value={fmtEur(kpis.adr)} sub="precio medio / noche vendida" to="/gestion/informes" />
          <StatCard icon={BarChart3} label="RevPAR" value={fmtEur(kpis.revpar)} sub="ingreso / habitación disponible" to="/gestion/informes" />
        </div>
      </div>

      {/* Gráfico ingresos 6 meses */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-serif">Ingresos últimos 6 meses</CardTitle>
            <p className="text-xs text-muted-foreground">Reservas con entrada en el mes (excluye canceladas)</p>
          </div>
          <Link to="/gestion/informes" className="text-xs text-primary hover:underline">Ver informes →</Link>
        </CardHeader>
        <CardContent>
          <IngresosChart data={serieMeses} />
        </CardContent>
      </Card>

      {/* Llegadas + columna lateral */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-serif">Próximas llegadas (14 días)</CardTitle>
            <Link to="/gestion/reservas" className="text-xs text-primary hover:underline">Ver todas →</Link>
          </CardHeader>
          <CardContent>
            {llegadas14.length === 0 ? (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                No hay llegadas próximas.
                <div className="mt-2"><Button asChild size="sm"><Link to="/gestion/reservas"><CalendarPlus className="mr-2 h-4 w-4" />Nueva reserva</Link></Button></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-muted-foreground">
                    <tr><th className="py-2">Huésped</th><th>Habitación</th><th>Entrada</th><th>Salida</th><th>Noches</th><th>Pax</th><th>Estado</th><th className="text-right">Precio</th></tr>
                  </thead>
                  <tbody>
                    {llegadas14.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="py-2 font-medium">{r.nombre}</td>
                        <td className="text-muted-foreground">{r.habitacion}</td>
                        <td>{fmtDate(r.fecha_entrada)}</td>
                        <td>{fmtDate(r.fecha_salida)}</td>
                        <td>{r.noches}</td>
                        <td>{r.personas}</td>
                        <td><EstadoBadge estado={r.estado} /></td>
                        <td className="text-right">{fmtEur(r.precio)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="font-serif text-base">Salidas próximas (7 días)</CardTitle></CardHeader>
            <CardContent>
              {salidas7.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin salidas previstas.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {salidas7.map((s) => (
                    <li key={s.id} className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{s.nombre}</div>
                        <div className="truncate text-xs text-muted-foreground">{s.habitacion}</div>
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">{fmtDate(s.fecha_salida)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-serif text-base">Acciones pendientes</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {todoAlDia ? (
                <p className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Todo al día</p>
              ) : (
                <>
                  {acciones.pendientes > 0 && (
                    <Link to="/gestion/reservas" className="flex items-start gap-2 hover:underline">
                      <AlertCircle className="mt-0.5 h-4 w-4 text-amber-600" />
                      <span>{acciones.pendientes} reserva(s) pendiente(s) de confirmar</span>
                    </Link>
                  )}
                  {acciones.habSinFotos > 0 && (
                    <Link to="/gestion/habitaciones" className="flex items-start gap-2 hover:underline">
                      <AlertCircle className="mt-0.5 h-4 w-4 text-amber-600" />
                      <span>{acciones.habSinFotos} habitación(es) sin fotos</span>
                    </Link>
                  )}
                  {acciones.habSinPrecio > 0 && (
                    <Link to="/gestion/habitaciones" className="flex items-start gap-2 hover:underline">
                      <AlertCircle className="mt-0.5 h-4 w-4 text-amber-600" />
                      <span>{acciones.habSinPrecio} habitación(es) sin precios definidos</span>
                    </Link>
                  )}
                  {acciones.sinTemporadas && (
                    <Link to="/gestion/tarifas" className="flex items-start gap-2 hover:underline">
                      <AlertCircle className="mt-0.5 h-4 w-4 text-amber-600" />
                      <span>Sin temporadas configuradas</span>
                    </Link>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Ocupación por habitación */}
      <Card>
        <CardHeader><CardTitle className="font-serif">Ocupación por habitación (próximos 30 días)</CardTitle></CardHeader>
        <CardContent>
          {ocupPorHab.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay habitaciones activas.</p>
          ) : (
            <div className="space-y-3">
              {ocupPorHab.map((h) => (
                <div key={h.id}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="font-medium">{h.nombre}</span>
                    <span className="text-muted-foreground">{h.noches} / {h.total} noches · {h.pct}%</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                    <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, h.pct)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Últimas reservas creadas */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-serif">Últimas reservas creadas</CardTitle>
          <CalendarCheck2 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {recientes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin reservas aún.</p>
          ) : (
            <ul className="divide-y text-sm">
              {recientes.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{r.nombre} <span className="text-muted-foreground">· {r.habitacion}</span></div>
                    <div className="text-xs text-muted-foreground">{fmtDate(r.fecha_entrada)} → {fmtDate(r.fecha_salida)} · {relTime(r.created_at)}</div>
                  </div>
                  <EstadoBadge estado={r.estado} />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}